import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { useTheme } from "../../../context/ThemeContext";
import { useAuth } from "../../../context/AuthContext";
import { getData as getCandidates } from "../../../models/CandidatesModel";
import { getData as getInterviews } from "../../../models/InterviewsModel";
import { getData as getOnboards } from "../../../models/OnboardsModel";
import { getCmsDataTableStyles } from "../../../helpers/DataTableStyles";
import TableLoader from "../../skeletons/TableLoader";
import { checkPermission } from "../../../helpers/GeneralHelper";
import {
  buildPipelinePrefillFromRequirement,
  buildPipelineSearchParams,
} from "../../../helpers/PipelineFilterHelper";
import dateFormat from "dateformat";
import { formatAMPM } from "../../../helpers/GeneralHelper";
import RequirementKanban from "./RequirementKanban";
import RequirementEligibleCandidates from "./RequirementEligibleCandidates";

const TABS = [
  { id: "overview", label: "Overview", icon: "bi-grid-1x2" },
  { id: "suggested", label: "Suggested", icon: "bi-person-check" },
  { id: "kanban", label: "Kanban", icon: "bi-kanban" },
  { id: "candidates", label: "Candidates", icon: "bi-people" },
  { id: "interviews", label: "Interviews", icon: "bi-briefcase" },
  { id: "onboards", label: "Onboards", icon: "bi-building" },
];

export default function RequirementPipeline({
  requirement,
  onAddCandidate,
  initialTab = "overview",
  highlightEligible = false,
}) {
  const { theme } = useTheme();
  const { state } = useAuth();
  const userToken = state.user_data?.user_token;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [counts, setCounts] = useState({
    submissions: null,
    interviews: null,
    onboards: null,
    loading: true,
  });

  const requirementId = requirement?._requirement_id;
  const clientId = requirement?.requirement_client_id;

  const pipelinePrefill = useMemo(
    () => buildPipelinePrefillFromRequirement(requirement),
    [requirement]
  );

  const fullListLink = (path) => ({
    pathname: path,
    search: `?${buildPipelineSearchParams(requirement)}`,
    state: { pipelinePrefill },
  });

  const positions = Number(requirement?.requirement_no_of_positions) || 0;
  const onHold = Number(requirement?.requirement_positions_hold) || 0;
  const closed = Number(requirement?.requirement_positions_closed) || 0;
  const availableFromData = Number(requirement?.positions_available_count);
  const available = Number.isFinite(availableFromData)
    ? availableFromData
    : Math.max(positions - onHold - closed, 0);

  const loadCounts = useCallback(async () => {
    if (!userToken || !requirementId) return;
    setCounts((prev) => ({ ...prev, loading: true }));

    try {
      const [candidatesRes, interviewsRes, onboardsRes] = await Promise.all([
        getCandidates({
          user_token: userToken,
          page: 1,
          limit: 1,
          candidate_client_id: clientId || "",
          candidate_requirement_id: requirementId,
          include_inactive_mappings: 1,
        }),
        getInterviews({
          user_token: userToken,
          page: 1,
          limit: 1,
          interview_client_id: clientId || "",
          interview_requirement_id: requirementId,
        }),
        getOnboards({
          user_token: userToken,
          page: 1,
          limit: 1,
          onboard_client_id: clientId || "",
          onboard_requirement_id: requirementId,
        }),
      ]);

      setCounts({
        submissions:
          candidatesRes?.pagination?.total_rows ??
          Number(requirement?.candidates_submitted_count) ??
          0,
        interviews:
          interviewsRes?.pagination?.total_rows ??
          Number(requirement?.interviews_count) ??
          0,
        onboards: onboardsRes?.pagination?.total_rows ?? 0,
        loading: false,
      });
    } catch (error) {
      console.error(error);
      setCounts((prev) => ({ ...prev, loading: false }));
    }
  }, [userToken, requirementId, clientId, requirement]);

  const loadTabData = useCallback(async () => {
    if (
      !userToken ||
      !requirementId ||
      activeTab === "overview" ||
      activeTab === "suggested" ||
      activeTab === "kanban"
    ) {
      return;
    }

    setLoading(true);
    try {
      let response;
      if (activeTab === "candidates") {
        response = await getCandidates({
          user_token: userToken,
          page,
          limit,
          candidate_client_id: clientId || "",
          candidate_requirement_id: requirementId,
          include_inactive_mappings: 1,
        });
      } else if (activeTab === "interviews") {
        response = await getInterviews({
          user_token: userToken,
          page,
          limit,
          interview_client_id: clientId || "",
          interview_requirement_id: requirementId,
        });
      } else if (activeTab === "onboards") {
        response = await getOnboards({
          user_token: userToken,
          page,
          limit,
          onboard_client_id: clientId || "",
          onboard_requirement_id: requirementId,
        });
      }

      if (response) {
        const key =
          activeTab === "candidates"
            ? "candidates"
            : activeTab === "interviews"
            ? "interviews"
            : "onboards";
        setRows(response[key] || []);
        setTotalRows(response.pagination?.total_rows || 0);
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  }, [activeTab, userToken, requirementId, clientId, page, limit]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab, requirementId]);

  useEffect(() => {
    if (!highlightEligible || activeTab !== "suggested") {
      return;
    }

    const timer = window.setTimeout(() => {
      document.getElementById("requirement-eligible-candidates")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 350);

    return () => window.clearTimeout(timer);
  }, [highlightEligible, activeTab, requirementId]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  const candidateColumns = [
    {
      name: "Candidate",
      selector: (row) =>
        `${row.candidate_firstname || ""} ${row.candidate_lastname || ""}`.trim(),
      sortable: true,
      wrap: true,
    },
    {
      name: "Status",
      selector: (row) => row.status_name,
      sortable: true,
      cell: (row) => (
        <span className="badge bg-warning text-black badge-wrap">{row.status_name}</span>
      ),
    },
    {
      name: "Recruiter",
      selector: (row) => row.recruiter_name,
      sortable: true,
    },
    {
      name: "Mobile",
      selector: (row) => row.candidate_mobile,
    },
  ];

  const interviewColumns = [
    { name: "Candidate", selector: (row) => row.candidate_name, sortable: true },
    {
      name: "Date",
      selector: (row) =>
        `${dateFormat(row.interview_date, "dd/mm/yyyy")} ${formatAMPM(
          new Date(`2024-04-28 ${row.interview_time}`)
        )}`,
      sortable: true,
    },
    { name: "Status", selector: (row) => row.status_name, sortable: true },
    { name: "Recruiter", selector: (row) => row.recruiter_name, sortable: true },
  ];

  const onboardColumns = [
    { name: "Candidate", selector: (row) => row.candidate_name, sortable: true },
    {
      name: "DOJ",
      selector: (row) => dateFormat(row.onboard_doj, "dd/mm/yyyy"),
      sortable: true,
    },
    {
      name: "Offboarded",
      selector: (row) => (row.onboard_is_offboard == 1 ? "Yes" : "No"),
    },
    { name: "Mobile", selector: (row) => row.candidate_mobile },
  ];

  const columns =
    activeTab === "candidates"
      ? candidateColumns
      : activeTab === "interviews"
      ? interviewColumns
      : onboardColumns;

  const handleRowClick = (row) => {
    if (activeTab === "candidates") {
      navigate(`/candidates/${row.candidate_unique_id}/view`, {
        state: {
          data: row,
          module: {
            title: "Candidates",
            singularTitle: "Candidate",
            key: "candidates",
            route: "candidates",
            primaryCol: "_candidate_id",
          },
        },
      });
    } else if (activeTab === "interviews") {
      navigate(`/interviews/${row.interview_unique_id}/view`, {
        state: {
          data: row,
          module: {
            title: "Interviews",
            singularTitle: "Interview",
            key: "interviews",
            route: "interviews",
            primaryCol: "_interview_id",
          },
        },
      });
    } else if (activeTab === "onboards") {
      navigate(`/onboards/${row.onboard_unique_id}/view`, {
        state: {
          data: row,
          module: {
            title: "Onboards",
            singularTitle: "Onboard",
            key: "onboards",
            route: "onboards",
            primaryCol: "_onboard_id",
          },
        },
      });
    }
  };

  const statValue = (value) =>
    counts.loading && value == null ? "…" : value ?? 0;

  return (
    <div className="requirement-pipeline-shell">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h6 className="mb-1 fw-semibold text-primary">Requirement pipeline</h6>
          <p className="text-muted small mb-0">
            Preview — track submissions, interviews, and onboards for this requirement in one place.
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {checkPermission("candidates", "add") && onAddCandidate && (
            <button type="button" className="btn btn-sm btn-primary" onClick={onAddCandidate}>
              <i className="ri-user-add-line me-1" />
              Add candidate
            </button>
          )}
        </div>
      </div>

      <div className="meeting-tabs requirement-pipeline-tabs mb-3">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`meeting-tab-btn${activeTab === tab.id ? " active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <i className={tab.icon} />
            {tab.label}
            {tab.id === "candidates" && counts.submissions != null && (
              <span className="badge bg-light text-primary ms-1">{counts.submissions}</span>
            )}
            {tab.id === "interviews" && counts.interviews != null && (
              <span className="badge bg-light text-primary ms-1">{counts.interviews}</span>
            )}
            {tab.id === "onboards" && counts.onboards != null && (
              <span className="badge bg-light text-primary ms-1">{counts.onboards}</span>
            )}
          </button>
        ))}
      </div>

      {activeTab === "kanban" && <RequirementKanban requirement={requirement} />}

      {activeTab === "suggested" && (
        <RequirementEligibleCandidates
          requirement={requirement}
          userToken={userToken}
          highlighted={highlightEligible}
          onAddCandidate={onAddCandidate}
        />
      )}

      {activeTab === "overview" && (
        <div className="requirement-pipeline-overview">
          <div className="row g-3 mb-4">
            <div className="col-md-3 col-6">
              <div className="pipeline-stat-card positions">
                <div className="pipeline-stat-label">Total positions</div>
                <div className="pipeline-stat-value">{positions}</div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="pipeline-stat-card available">
                <div className="pipeline-stat-label">Available</div>
                <div className="pipeline-stat-value">{available}</div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="pipeline-stat-card submissions">
                <div className="pipeline-stat-label">Submissions</div>
                <div className="pipeline-stat-value">{statValue(counts.submissions)}</div>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="pipeline-stat-card interviews">
                <div className="pipeline-stat-label">Interviews</div>
                <div className="pipeline-stat-value">{statValue(counts.interviews)}</div>
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-md-4">
              <div className="pipeline-meta-card">
                <div className="text-muted small">Status</div>
                <div className="fw-semibold">{requirement?.status_name || "—"}</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="pipeline-meta-card">
                <div className="text-muted small">On hold / Closed</div>
                <div className="fw-semibold">
                  {onHold} / {closed}
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="pipeline-meta-card">
                <div className="text-muted small">Onboards</div>
                <div className="fw-semibold">{statValue(counts.onboards)}</div>
              </div>
            </div>
          </div>

          <div className="mt-4 d-flex flex-wrap gap-2 align-items-center">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => setActiveTab("suggested")}
            >
              <i className="bi bi-person-check me-1" />
              View suggested candidates
            </button>
          </div>

          <div className="mt-3 d-flex flex-wrap gap-2">
            <Link to={fullListLink("/candidates")} className="btn btn-sm btn-outline-primary">
              Open candidates list
            </Link>
            <Link to={fullListLink("/interviews")} className="btn btn-sm btn-outline-primary">
              Open interviews list
            </Link>
            <Link to={fullListLink("/onboards")} className="btn btn-sm btn-outline-primary">
              Open onboards list
            </Link>
          </div>
        </div>
      )}

      {activeTab !== "overview" && activeTab !== "suggested" && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted small">
              Click a row to open the record. Showing data for this requirement only.
            </span>
            <Link
              to={fullListLink(
                activeTab === "candidates"
                  ? "/candidates"
                  : activeTab === "interviews"
                  ? "/interviews"
                  : "/onboards"
              )}
              className="btn btn-sm btn-light"
            >
              Full list <i className="bi bi-box-arrow-up-right ms-1" />
            </Link>
          </div>
          <div className="table-responsive">
            <DataTable
              className="clickable cms-data-table"
              columns={columns}
              data={rows}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={limit}
              paginationRowsPerPageOptions={[10, 25, 50]}
              onChangePage={setPage}
              onChangeRowsPerPage={(newLimit, newPage) => {
                setLimit(newLimit);
                setPage(newPage);
              }}
              progressPending={loading}
              progressComponent={<TableLoader noOfColumn={columns.length} />}
              onRowClicked={handleRowClick}
              highlightOnHover
              theme={theme === "light" ? "light" : "dark"}
              customStyles={getCmsDataTableStyles(theme)}
              fixedHeader
              fixedHeaderScrollHeight="420px"
            />
          </div>
        </>
      )}
    </div>
  );
}
